import type { CSSProperties } from 'react';
import { useTranslation } from 'react-i18next';
import type { DropResult } from 'react-beautiful-dnd';

type UseDraggableItemsListParams = {
  disableDrag?: boolean;
  disableEditMode?: boolean;
  handleOnDragEnd: (result: DropResult) => void;
};

export type UseDraggableItemsListReturn = {
  dragHandleLabel: string;
  getListStyle: (isDraggingOver: boolean) => CSSProperties;
  isDragDisabled: boolean;
  onDragEnd: (result: DropResult) => void;
};

// Resaltado al arrastrar por encima: mismo token de fondo-hover de opción del
// DS v2 (`--ui-color-bg-option-hover`), tematizable por tenant. Se aplica como
// style inline porque depende del estado de arrastre de react-beautiful-dnd.
// Función pura (no cierra sobre estado del hook): vive a nivel de módulo.
function getListStyle(isDraggingOver: boolean): CSSProperties {
  return {
    background: isDraggingOver ? 'var(--ui-color-bg-option-hover)' : 'transparent',
    borderRadius: 'var(--ui-radius-xl)',
    paddingBottom: isDraggingOver ? 'var(--ui-space-6xl)' : undefined,
    userSelect: 'none',
  };
}

function useDraggableItemsList({
  disableDrag = false,
  disableEditMode = true,
  handleOnDragEnd,
}: UseDraggableItemsListParams): UseDraggableItemsListReturn {
  const { t } = useTranslation();

  const isDragDisabled = disableEditMode || disableDrag;

  function onDragEnd(result: DropResult): void {
    handleOnDragEnd(result);
  }

  const dragHandleLabel = t('common:DragToReorder');

  return { dragHandleLabel, getListStyle, isDragDisabled, onDragEnd };
}

export default useDraggableItemsList;
